import i18next from 'i18next';
import { SearchResult, DeepAnalysisExportData, DeepAnalysisTimelineItem } from '../services/api';

export function exportToCSV(results: SearchResult[], filename: string = 'wyniki-wyszukiwania.csv') {
  const headers = ['ID', 'Sygnatura', 'Data', 'Typ sądu', 'Podobieństwo', 'Fragment'];

  const rows = results.map((result) => [
    result.id.toString(),
    result.caseNumber,
    formatDate(result.judgmentDate),
    result.courtType,
    (result.similarity * 100).toFixed(1) + '%',
    `"${result.snippet.replace(/"/g, '""').replace(/\n/g, ' ')}"`,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
}

export function exportToJSON(results: SearchResult[], query: string, filename: string = 'wyniki-wyszukiwania.json') {
  const data = {
    query,
    exportedAt: new Date().toISOString(),
    resultCount: results.length,
    results: results.map((result) => ({
      id: result.id,
      judgmentId: result.judgmentId,
      caseNumber: result.caseNumber,
      judgmentDate: result.judgmentDate,
      courtType: result.courtType,
      similarity: result.similarity,
      snippet: result.snippet,
      chunkType: result.chunkType,
    })),
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  downloadBlob(blob, filename);
}

export function exportToHTML(results: SearchResult[], query: string, filename: string = 'wyniki-wyszukiwania.html') {
  const courtTypeLabels: Record<string, string> = {
    SUPREME: 'Sąd Najwyższy',
    COMMON: 'Sąd Powszechny',
    CONSTITUTIONAL_TRIBUNAL: 'Trybunał Konstytucyjny',
    NATIONAL_APPEAL_CHAMBER: 'Krajowa Izba Odwoławcza',
  };

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <title>Wyniki wyszukiwania: ${escapeHtml(query)}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; }
    h1 { color: #1e293b; }
    .meta { color: #64748b; margin-bottom: 20px; }
    .result { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 12px; }
    .result-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
    .case-number { font-weight: 600; color: #1e293b; }
    .similarity { background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; font-size: 12px; }
    .court-type { color: #64748b; font-size: 14px; }
    .snippet { color: #475569; font-size: 14px; line-height: 1.6; }
  </style>
</head>
<body>
  <h1>Wyniki wyszukiwania</h1>
  <p class="meta">
    Zapytanie: <strong>${escapeHtml(query)}</strong><br>
    Liczba wyników: ${results.length}<br>
    Data eksportu: ${new Date().toLocaleDateString((i18next.language === 'pl' ? 'pl-PL' : 'en-US'))}
  </p>
  ${results.map((result) => `
  <div class="result">
    <div class="result-header">
      <div>
        <span class="case-number">${escapeHtml(result.caseNumber)}</span>
        <span class="court-type">${courtTypeLabels[result.courtType] || result.courtType}</span>
      </div>
      <span class="similarity">${(result.similarity * 100).toFixed(1)}%</span>
    </div>
    <div class="snippet">${escapeHtml(result.snippet)}</div>
  </div>
  `).join('')}
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  downloadBlob(blob, filename);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDate(dateString: string): string {
  try {
    return new Date(dateString).toLocaleDateString((i18next.language === 'pl' ? 'pl-PL' : 'en-US'));
  } catch {
    return dateString;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Convert markdown to HTML (basic support)
 */
function markdownToHtml(markdown: string): string {
  let html = markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h4>$1</h4>')
    .replace(/^## (.*$)/gim, '<h3>$1</h3>')
    .replace(/^# (.*$)/gim, '<h2>$1</h2>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic (single asterisk, but not inside bold)
    .replace(/(?<!\*)\*(?!\*)([^*]+)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    // Blockquotes
    .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Handle numbered lists: convert "1. item" to list items
  html = html.replace(/(?:^|<br>)(\d+)\. ([^<]+)(?=<br>|$)/g, '<li>$2</li>');
  // Wrap consecutive <li> elements in <ol>
  html = html.replace(/(<li>.*?<\/li>)+/g, '<ol>$&</ol>');

  // Handle bullet lists: convert "- item" to list items
  html = html.replace(/(?:^|<br>)- ([^<]+)(?=<br>|$)/g, '<li class="bullet">$1</li>');
  // Wrap bullet <li> elements in <ul>
  html = html.replace(/(<li class="bullet">.*?<\/li>)+/g, '<ul>$&</ul>');
  // Clean up the bullet class
  html = html.replace(/ class="bullet"/g, '');

  // Clean up empty paragraphs and extra breaks
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<br><(ol|ul|h[234]|blockquote)/g, '<$1');
  html = html.replace(/<\/(ol|ul|h[234]|blockquote)><br>/g, '</$1>');

  return html;
}

/**
 * Get label for timeline item type
 */
function getTimelineItemLabel(type: DeepAnalysisTimelineItem['type']): { label: string; color: string } {
  switch (type) {
    case 'user':
      return { label: 'Uzytkownik', color: '#059669' };
    case 'assistant':
      return { label: 'Asystent AI', color: '#7c3aed' };
    case 'clarification':
      return { label: 'Doprecyzowanie', color: '#d97706' };
    case 'report':
      return { label: 'Raport koncowy', color: '#1e40af' };
    default:
      return { label: 'Wiadomosc', color: '#64748b' };
  }
}

/**
 * Export deep analysis session to HTML
 */
export function exportDeepAnalysisToHTML(
  data: DeepAnalysisExportData,
  filename: string = 'analiza-gleboka.html'
) {
  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <title>Raport z gleobkiej analizy - ${escapeHtml(data.sessionId)}</title>
  <style>
    :root {
      --primary: #1e40af;
      --text: #1e293b;
      --muted: #64748b;
      --border: #e2e8f0;
      --bg: #f8fafc;
    }
    body {
      font-family: system-ui, -apple-system, sans-serif;
      max-width: 900px;
      margin: 0 auto;
      padding: 40px 20px;
      color: var(--text);
      line-height: 1.6;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 20px;
      border-bottom: 2px solid var(--border);
    }
    .header h1 {
      color: var(--primary);
      margin: 0 0 8px 0;
      font-size: 28px;
    }
    .header .subtitle {
      color: var(--muted);
      font-size: 14px;
    }
    .meta {
      background: var(--bg);
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 32px;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 12px;
    }
    .meta-item {
      display: flex;
      flex-direction: column;
    }
    .meta-label {
      font-size: 12px;
      color: var(--muted);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .meta-value {
      font-weight: 500;
    }
    .section {
      margin-bottom: 32px;
    }
    .section-title {
      color: var(--primary);
      font-size: 18px;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border);
    }
    .original-query {
      background: var(--bg);
      padding: 16px;
      border-radius: 8px;
      border-left: 4px solid var(--primary);
    }
    .timeline-item {
      margin-bottom: 24px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid var(--border);
    }
    .timeline-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
    }
    .timeline-label {
      font-weight: 600;
      font-size: 14px;
    }
    .timeline-time {
      color: var(--muted);
      font-size: 12px;
    }
    .timeline-content {
      font-size: 14px;
    }
    .timeline-content h2, .timeline-content h3, .timeline-content h4 {
      color: var(--primary);
      margin-top: 16px;
      margin-bottom: 8px;
    }
    .timeline-content h2 { font-size: 18px; }
    .timeline-content h3 { font-size: 16px; }
    .timeline-content h4 { font-size: 14px; }
    .timeline-content ul {
      margin: 8px 0;
      padding-left: 24px;
    }
    .timeline-content li {
      margin-bottom: 4px;
    }
    .timeline-content blockquote {
      margin: 8px 0;
      padding: 8px 16px;
      border-left: 3px solid var(--border);
      color: var(--muted);
      font-style: italic;
    }
    .report-item {
      background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
      border-color: var(--primary);
    }
    .footer {
      text-align: center;
      padding-top: 32px;
      border-top: 1px solid var(--border);
      color: var(--muted);
      font-size: 12px;
    }
    @media print {
      body { padding: 0; }
      .timeline-item { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>AnalizaPrawna</h1>
    <p class="subtitle">Raport z gleobkiej analizy prawnej</p>
  </div>

  <div class="meta">
    <div class="meta-grid">
      <div class="meta-item">
        <span class="meta-label">ID sesji</span>
        <span class="meta-value">${escapeHtml(data.sessionId)}</span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Data utworzenia</span>
        <span class="meta-value">${new Date(data.createdAt).toLocaleString((i18next.language === 'pl' ? 'pl-PL' : 'en-US'))}</span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Data eksportu</span>
        <span class="meta-value">${new Date().toLocaleString((i18next.language === 'pl' ? 'pl-PL' : 'en-US'))}</span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Zuzycie tokenow</span>
        <span class="meta-value">${data.tokensUsed.toLocaleString((i18next.language === 'pl' ? 'pl-PL' : 'en-US'))}</span>
      </div>
    </div>
  </div>

  <div class="section">
    <h2 class="section-title">Zapytanie poczatkowe</h2>
    <div class="original-query">${escapeHtml(data.originalQuery)}</div>
  </div>

  <div class="section">
    <h2 class="section-title">Przebieg analizy</h2>
    ${data.timeline.map(item => {
      const { label, color } = getTimelineItemLabel(item.type);
      const isReport = item.type === 'report';
      // Apply markdown rendering for report and assistant (followup) responses
      const useMarkdown = isReport || item.type === 'assistant';
      const content = useMarkdown ? markdownToHtml(item.content) : escapeHtml(item.content);
      return `
    <div class="timeline-item${isReport ? ' report-item' : ''}">
      <div class="timeline-header">
        <span class="timeline-label" style="color: ${color}">${label}</span>
        <span class="timeline-time">${new Date(item.timestamp).toLocaleTimeString((i18next.language === 'pl' ? 'pl-PL' : 'en-US'))}</span>
      </div>
      <div class="timeline-content">${content}</div>
    </div>`;
    }).join('\n')}
  </div>

  <div class="footer">
    <p>Wygenerowano przez AnalizaPrawna - dane pochodza z publicznych rejestrow sadowych</p>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  downloadBlob(blob, filename);
}
